/**
 * @file price-import-tab.tsx
 * @module app/modules/setting/price-import-tab
 * @description Price Import tab for uploading and reconciling product price
 * update excel/csv files. Copied from the deprecated
 * app/modules/settings/price-update-excel.tsx.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.9
 * @version 2.1.2
 */

import React, { useState, useRef } from 'react';
import { Button, Card, Col, Form, Row, Table, Alert } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import APICallService from '../../../api/apiCallService';
import { manageProduct } from '../../../api/apiEndPoints';
import { AllModules } from '../../../utils/constants';
import { success, error } from '../../../Global/toast';

/**
 * Price import tab component.
 * @returns {JSX.Element} Price import tab content.
 */
const PriceImportTab = () => {
  const [file, setFile] = useState<File | null>(null);
  const [fileData, setFileData] = useState<any[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState<string>('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  // API response
  const [matched, setMatched] = useState<any[]>([]);
  const [unmatched, setUnmatched] = useState<any[]>([]);
  const [apiDone, setApiDone] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadSample = async () => {
    setIsDownloading(true);
    try {
      const apiService = new APICallService(
        manageProduct.downloadPriceImportSample,
        undefined,
        undefined,
        'arraybuffer'
      );
      const response = await apiService.callAPI();
      if (response) {
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'price_import_sample.xlsx';
        link.click();
        URL.revokeObjectURL(url);
      } else {
        error('Failed to download sample file');
      }
    } catch (err) {
      error('Failed to download sample file');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setApiDone(false);
    setMatched([]);
    setUnmatched([]);
    setValidationError('');

    const selectedFile = e.target.files?.[0];
    if (!selectedFile) {
      setFile(null);
      setFileData([]);
      setColumns([]);
      setIsValid(false);
      return;
    }

    setFile(selectedFile);

    try {
      const bstr = await selectedFile.arrayBuffer();
      const wb = XLSX.read(bstr, { type: 'array' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      // defval keeps every header key present per row (blank cells become ''), otherwise rows with
      // empty Slab/Percentage cells would drop those keys and hide the columns in the preview table
      const data: any[] = XLSX.utils.sheet_to_json(ws, { defval: '' });

      if (data && data.length > 0) {
        // Check columns (case-insensitive keys)
        const firstRow = data[0];
        const receivedCols = new Set(Object.keys(firstRow).map((k) =>
          k.toLowerCase().trim()
        ));

        const hasWarehouse =
          receivedCols.has('warehouse name') ||
          receivedCols.has('warehouse');
        const hasProduct =
          receivedCols.has('product name') ||
          receivedCols.has('product') ||
          receivedCols.has('title') ||
          receivedCols.has('name');
        const hasUnit = receivedCols.has('unit');
        const hasBasePrice = receivedCols.has('base price');
        const hasDiscountPrice = receivedCols.has('discount price');
        const hasPercentage = receivedCols.has('percentage');
        const hasSlab =
          receivedCols.has('slab from') &&
          receivedCols.has('slab to') &&
          receivedCols.has('slab price');

        if (
          hasWarehouse &&
          hasProduct &&
          hasUnit &&
          (hasBasePrice || hasDiscountPrice || hasPercentage || hasSlab)
        ) {
          setColumns(Object.keys(firstRow));
          setFileData(data);
          setIsValid(true);
          setValidationError('');
        } else {
          setIsValid(false);
          setValidationError(
            "File must have columns 'Warehouse Name', 'Product Name', 'Unit', and at least one of 'Base Price', 'Discount Price', 'Percentage', or 'Slab From'/'Slab To'/'Slab Price'"
          );
          setFileData([]);
          setColumns([]);
        }
      } else {
        setIsValid(false);
        setValidationError('The uploaded file is empty.');
        setFileData([]);
        setColumns([]);
      }
    } catch (err) {
      // The parser exception is intentionally ignored because its details are not actionable; show a generic validation error instead.
      setIsValid(false);
      setValidationError('Error parsing file.');
      setFileData([]);
      setColumns([]);
    }
  };

  const handleSubmit = async () => {
    if (!file || !isValid) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const apiService = new APICallService(
        manageProduct.reconcileExcel,
        formData,
        '',
        '',
        '',
        '',
        AllModules
      );

      const response = await apiService.callAPI();

      if (response) {
        success('Excel file processed successfully');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setFile(null);
        setMatched(response.matched || []);
        setUnmatched(response.unmatched || []);
        setApiDone(true);
      }
    } catch (err) {
      error('Failed to process excel file');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-5">
      <Row className="align-items-center mb-5">
        <Col md>
          <h1 className="fs-22 fw-bolder">Menu Price Update Excel</h1>
        </Col>
        <Col md="auto">
          <Button className="uw-btn uw-btn-primary uw-btn-sm"
            variant="primary"
            size="sm"
            onClick={handleDownloadSample}
            disabled={isDownloading}
          >
            {isDownloading ? 'Downloading...' : 'Download Sample File'}
          </Button>
        </Col>
      </Row>

      <Card className="bg-light border mb-7">
        <Card.Body className="px-7">
          <Row className="mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fs-16 fw-500">
                  Upload Excel / CSV File
                </Form.Label>
                <Form.Control
                  type="file"
                  accept=".xlsx, .csv"
                  onChange={handleFileChange}
                  onClick={(e: any) => {
                    e.target.value = null;
                  }}
                  ref={fileInputRef}
                />
              </Form.Group>
            </Col>
          </Row>

          {validationError && <Alert variant="danger">{validationError}</Alert>}

          {isValid && fileData.length > 0 && !apiDone && (
            <div className="mt-5">
              <h4 className="mb-3">File Preview (Showing top 100 rows)</h4>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Table striped bordered hover responsive className="bg-white">
                  <thead>
                    <tr>
                      {columns.map((col, idx) => (
                        <th key={idx}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fileData.map((row, idx) => (
                      <tr key={idx}>
                        {columns.map((col, cIdx) => (
                          <td key={cIdx}>{row[col]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          {apiDone && (
            <div className="mt-5">
              <h4 className="text-success mb-3">Modifcation Results</h4>

              <div className="mb-5">
                <h5>Matched Products: {matched.length}</h5>
                {matched.length > 0 && (
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <Table
                      striped
                      bordered
                      hover
                      responsive
                      size="sm"
                      className="bg-white"
                    >
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th>Unit</th>
                          <th>Excel Warehouse</th>
                          <th>Main Warehouse</th>
                          <th>Base Price</th>
                          <th>Discount Price</th>
                          <th>Percentage</th>
                          <th>Slab From</th>
                          <th>Slab To</th>
                          <th>Slab Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {matched.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.title}</td>
                            <td>{item.unit}</td>
                            <td>{item.excelWarehouse}</td>
                            <td>{item.isDefaultWarehouse ? 'Yes' : 'No'}</td>
                            <td>{item.basePrice ?? '-'}</td>
                            <td>{item.discountPrice ?? '-'}</td>
                            <td>{item.percentage ?? '-'}</td>
                            <td>{item.slabFrom ?? '-'}</td>
                            <td>{item.slabTo ?? '-'}</td>
                            <td>{item.slabPrice ?? '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>

              <div>
                <h5 className="text-danger">
                  Unmatched Products: {unmatched.length}
                </h5>
                {unmatched.length > 0 && (
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <Table
                      striped
                      bordered
                      hover
                      responsive
                      size="sm"
                      className="bg-white"
                    >
                      <thead>
                        <tr>
                          {Object.keys(unmatched[0]).map((col, i) => (
                            <th key={i}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {unmatched.map((item, idx) => (
                          <tr key={idx}>
                            {Object.values(item).map((val: any, cIdx) => (
                              <td key={cIdx}>{val?.toString()}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}

          {!apiDone && isValid && (
            <Row className="mt-5">
              <Col xs={3}>
                <Button className="uw-btn uw-btn-primary uw-btn-lg"
                  variant="primary"
                  size="lg"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {!loading && <span className="indicator-label">Submit</span>}
                  {loading && (
                    <span
                      className="indicator-progress"
                      style={{ display: 'block' }}
                    >
                      Please wait...
                      <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  )}
                </Button>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default PriceImportTab;